import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Zap, ArrowRight, CheckCircle, Loader2, Mail } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";
import snakeLogo from "@/assets/snake-signals-logo-clean.png";

const emailSchema = z.string().email("Please enter a valid email address").max(255);

interface InlineSubscribeFormProps {
  variant?: "default" | "compact" | "minimal";
  buttonText?: string;
  showIcon?: boolean;
  className?: string;
}

export const InlineSubscribeForm = ({ 
  variant = "default",
  buttonText = "Subscribe",
  showIcon = true,
  className = ""
}: InlineSubscribeFormProps) => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email
    const result = emailSchema.safeParse(email.trim());
    if (!result.success) {
      toast({
        title: "Invalid email",
        description: result.error.errors[0].message,
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('subscribe-newsletter', {
        body: { email: email.trim() }
      });

      if (error) {
        throw new Error(error.message || 'Failed to subscribe');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      setIsSuccess(true);
      setEmail("");
      toast({
        title: "You're in! 🎉",
        description: data?.message || "Check your inbox (and spam folder) for a welcome email.",
      });

      // Reset success state after 8 seconds
      setTimeout(() => setIsSuccess(false), 8000);

    } catch (error: any) {
      console.error('Subscribe error:', error);
      toast({
        title: "Subscription failed",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    // Generate confetti particles with different angles and colors
    const confettiColors = [
      'bg-primary',
      'bg-accent', 
      'bg-green-500',
      'bg-yellow-400',
      'bg-purple-500',
      'bg-cyan-400',
    ];
    
    return (
      <div className={`flex flex-col items-center justify-center gap-4 py-4 ${className}`}>
        {/* Animated snake logo with glow and confetti */}
        <div className="relative">
          {/* Confetti particles */}
          {[...Array(12)].map((_, i) => {
            const angle = i * 30;
            const delay = i * 50;
            const distance = 50 + Math.random() * 20;
            const x = Math.cos((angle * Math.PI) / 180) * distance;
            const y = Math.sin((angle * Math.PI) / 180) * distance;
            
            return (
              <div
                key={i}
                className={`absolute left-1/2 top-1/2 w-2 h-2 rounded-full ${confettiColors[i % 6]} pointer-events-none`}
                style={{
                  '--x': `${x}px`,
                  '--y': `${y}px`,
                  animation: `confetti-explode 0.8s ease-out ${delay}ms forwards`,
                } as React.CSSProperties}
              />
            );
          })}
          
          {/* Outer glow ring */}
          <div className="absolute -inset-4 bg-primary/20 rounded-full animate-ping opacity-50" />
          <div className="absolute -inset-2 bg-gradient-to-r from-primary/30 via-accent/30 to-primary/30 rounded-full blur-md animate-pulse" />
          
          {/* Snake logo with bounce */}
          <div className="relative animate-scale-in">
            <img 
              src={snakeLogo} 
              alt="Snake Signals" 
              className="w-16 h-16 object-contain drop-shadow-[0_0_15px_hsl(var(--primary)/0.5)]"
            />
            {/* Checkmark overlay */}
            <div className="absolute -bottom-1 -right-1 bg-primary rounded-full p-1 animate-scale-in shadow-lg">
              <CheckCircle className="w-5 h-5 text-primary-foreground" />
            </div>
          </div>
        </div>
        
        {/* Success text */}
        <div className="flex flex-col items-center gap-1 animate-fade-in text-center">
          <span className="font-bold text-xl bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
            You're in! 🐍✨
          </span>
          <span className="text-sm text-muted-foreground max-w-[250px]">
            Welcome email sent! Check your inbox
          </span>
          <span className="text-xs text-muted-foreground/70 flex items-center gap-1 mt-1">
            <Mail className="w-3 h-3" />
            Not there? Check your spam folder
          </span>
        </div>
      </div>
    );
  }

  if (variant === "minimal") {
    return (
      <form onSubmit={handleSubmit} className={`flex gap-2 ${className}`}>
        <Input
          type="email"
          placeholder="your@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !isLoading && handleSubmit(e)}
          className="flex-1"
          disabled={isLoading}
          required
        />
        <Button type="submit" size="sm" disabled={isLoading}>
          {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
        </Button>
      </form>
    );
  }

  if (variant === "compact") {
    return (
      <form onSubmit={handleSubmit} className={`flex gap-2 ${className}`}>
        <Input
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !isLoading && handleSubmit(e)}
          className="flex-1 max-w-xs"
          disabled={isLoading}
          required
        />
        <Button type="submit" disabled={isLoading} className="gap-2">
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              {showIcon && <Zap className="w-4 h-4" />}
              {buttonText}
            </>
          )}
        </Button>
      </form>
    );
  }

  // Default variant - full width with larger elements
  return (
    <form onSubmit={handleSubmit} className={`space-y-3 ${className}`}>
      <div className="flex flex-col sm:flex-row gap-3">
        <Input
          type="email"
          placeholder="Enter your email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !isLoading && handleSubmit(e)}
          className="flex-1"
          disabled={isLoading}
          required
        />
        <Button type="submit" size="lg" disabled={isLoading} className="gap-2 whitespace-nowrap">
          {isLoading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              {showIcon && <Zap className="w-5 h-5" />}
              {buttonText}
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground text-center sm:text-left">
        No spam. Unsubscribe anytime. Data handled with care.
      </p>
    </form>
  );
};
